import { RefObject, useEffect } from "react";

import IntelligemsWatermark from "@src/images/igLogo/intelligems-watermark.svg?raw";

const generateWatermarkElement = () => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(IntelligemsWatermark, "image/svg+xml");
  const svgElement = doc.documentElement;
  svgElement.style.opacity = "0.5";

  return svgElement;
};

type useAddGraphWatermarkProps = {
  containerRef: RefObject<HTMLDivElement | null>;
  isDataLoading: boolean;
  position?: "left" | "right";
  showWatermark: boolean | undefined;
};

export const useAddGraphWatermark = ({
  containerRef,
  isDataLoading,
  position = "right",
  showWatermark,
}: useAddGraphWatermarkProps): void => {
  useEffect(() => {
    if (isDataLoading || !showWatermark) {
      return;
    }

    let rafId: number | null = null;
    let cancelled = false;

    const addWatermark = () => {
      // Check if this effect has been cancelled
      if (cancelled) {
        return;
      }

      if (!containerRef.current) {
        return;
      }

      const cartesianGrid = containerRef.current.querySelector(
        ".recharts-cartesian-grid"
      );
      const surface = containerRef.current.querySelector(".recharts-surface");
      const referenceElement = cartesianGrid || surface; // Not all graphs have a recharts-cartesian-grid but they always have a recharts-surface

      // If Recharts hasn't rendered yet, retry on next frame
      if (!referenceElement) {
        rafId = requestAnimationFrame(addWatermark);
        return;
      }

      // Check if the element has dimensions (is painted)
      const { height } = referenceElement.getBoundingClientRect();
      if (height === 0) {
        rafId = requestAnimationFrame(addWatermark);
        return;
      }

      // Check if watermark already exists to prevent duplicates
      const existingWatermark = referenceElement.querySelector(
        'svg[style*="opacity: 0.5"]'
      );
      if (existingWatermark) {
        return;
      }

      const watermarkElement = generateWatermarkElement();
      referenceElement.append(watermarkElement);

      const { height: referenceElementHeight } =
        referenceElement.getBoundingClientRect();
      const { height: watermarkHeight, width: watermarkWidth } =
        watermarkElement.getBoundingClientRect();

      const YPos = referenceElementHeight / 2 - watermarkHeight / 2;
      const XPosBuffer = `4px`;
      const XPos =
        position === "right"
          ? `calc(100% - ${watermarkWidth}px - ${XPosBuffer})`
          : XPosBuffer;
      watermarkElement.style.transform = `translate(${XPos}, ${YPos}px)`;
    };

    // Use requestAnimationFrame to ensure charts have rendered
    rafId = requestAnimationFrame(addWatermark);

    // Cleanup function to remove watermark elements and cancel pending animation frames
    return () => {
      cancelled = true;

      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }

      if (!containerRef.current) return;

      const existingWatermarks = containerRef.current.querySelectorAll(
        'svg[style*="opacity: 0.5"]'
      );
      existingWatermarks.forEach((watermark) => {
        watermark.remove();
      });
    };
  }, [isDataLoading, showWatermark, containerRef, position]);
};
